import React from 'react';
import SectionHeader from './SectionHeader';
import { fertilityDemoEditButton, fertilityDemoSaveButton, FERTILITY_DEMO_SECTION_BORDER } from '../designSystem';

const DIAGNOSIS_OPTIONS = ['Unexplained', 'PCOS', 'DOR (low ovarian reserve)', 'Endometriosis', 'Male factor', 'Tubal factor', 'Other'];
const PROTOCOL_OPTIONS = ['Antagonist', 'Long Lupron', 'Micro-dose Lupron', 'Mini IVF', 'Natural cycle', 'Other'];

function DataRow({ label, value, borderColor, isLast }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: isLast ? 'none' : `1px solid ${borderColor}` }}>
      <span style={{ fontSize: 12, color: '#2d1a22' }}>{label}</span>
      <span style={{ fontSize: 12, color: 'rgba(60,30,45,0.4)', fontStyle: 'italic' }}>{value}</span>
    </div>
  );
}

function DataRowSage({ label, value, isLast }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: isLast ? 'none' : '1px solid rgba(29, 158, 117, 0.12)' }}>
      <span style={{ fontSize: 12, color: '#0d2e24' }}>{label}</span>
      <span style={{ fontSize: 12, color: 'rgba(10,50,35,0.4)', fontStyle: 'italic' }}>{value}</span>
    </div>
  );
}

/** Editable journey summary — diagnosis, protocol, IVF/TTC details. Nova uses this to personalize support. */
export default function MyStoryJourney({
  user,
  timeline,
  journey,
  chapter,
  getJourneyTimeline,
  updateJourneyTimeline,
  navigate,
  fontSerif,
  fontBody,
  chapterInfo,
  embedded = false,
  hideLinks = false,
  /** Match `/demo/fertility` home section cards — pink keyline */
  fertilityDemoSectionKeyline = false,
}) {
  const [editing, setEditing] = React.useState(false);
  const proto = timeline?.ivf_protocol || {};
  const cycleData = timeline?.cycle_data || {};
  const keyDates = timeline?.key_dates || {};
  const isIVF = chapter === 'fertility' || chapter === 'ivf';
  const isTTC = chapter === 'ttc' || chapter === 'trying-to-conceive';

  const [form, setForm] = React.useState({
    cycle_number: proto.cycle_number ?? (journey?.cycles?.filter((c) => c.stage === 'ivf').length || 1),
    cycle_day: proto.cycle_day ?? 0,
    follicles: proto.follicles ?? 0,
    eggs_retrieved: proto.eggs_retrieved ?? 0,
    fertilized: proto.fertilized ?? 0,
    embryos: proto.embryos ?? 0,
    transfers: proto.transfers ?? 0,
    protocol_type: proto.protocol_type ?? '',
    clinic_name: proto.clinic_name ?? '',
    diagnosis: proto.diagnosis ?? '',
    partner_factor: proto.partner_factor ?? '',
    stim_meds: proto.stim_meds ?? '',
    amh: proto.amh ?? '',
    prior_losses: proto.prior_losses ?? '',
    retrieval_date: proto.retrieval_date || keyDates.retrieval_date || '',
    transfer_date: proto.transfer_date || keyDates.transfer_date || '',
    months_trying: cycleData.months_trying ?? 0,
    avg_cycle_length: cycleData.avg_cycle_length ?? 28,
    last_period_date: cycleData.last_period_date || '',
    tracking_method: cycleData.tracking_method ?? '',
  });

  React.useEffect(() => {
    const p = timeline?.ivf_protocol || {};
    const c = timeline?.cycle_data || {};
    const k = timeline?.key_dates || {};
    setForm({
      cycle_number: p.cycle_number ?? (journey?.cycles?.filter((x) => x.stage === 'ivf').length || 1),
      cycle_day: p.cycle_day ?? 0,
      follicles: p.follicles ?? 0,
      eggs_retrieved: p.eggs_retrieved ?? 0,
      fertilized: p.fertilized ?? 0,
      embryos: p.embryos ?? 0,
      transfers: p.transfers ?? 0,
      protocol_type: p.protocol_type ?? '',
      clinic_name: p.clinic_name ?? '',
      diagnosis: p.diagnosis ?? '',
      partner_factor: p.partner_factor ?? '',
      stim_meds: p.stim_meds ?? '',
      amh: p.amh ?? '',
      prior_losses: p.prior_losses ?? '',
      retrieval_date: p.retrieval_date || k.retrieval_date || '',
      transfer_date: p.transfer_date || k.transfer_date || '',
      months_trying: c.months_trying ?? 0,
      avg_cycle_length: c.avg_cycle_length ?? 28,
      last_period_date: c.last_period_date || '',
      tracking_method: c.tracking_method ?? '',
    });
  }, [timeline, journey?.cycles]);

  function save() {
    updateJourneyTimeline({
      ivf_protocol: {
        cycle_number: form.cycle_number ? Number(form.cycle_number) : undefined,
        cycle_day: form.cycle_day ? Number(form.cycle_day) : undefined,
        follicles: form.follicles ? Number(form.follicles) : undefined,
        eggs_retrieved: form.eggs_retrieved ? Number(form.eggs_retrieved) : undefined,
        fertilized: form.fertilized ? Number(form.fertilized) : undefined,
        embryos: form.embryos ? Number(form.embryos) : undefined,
        transfers: form.transfers ? Number(form.transfers) : undefined,
        protocol_type: form.protocol_type || undefined,
        clinic_name: form.clinic_name || undefined,
        diagnosis: form.diagnosis || undefined,
        partner_factor: form.partner_factor || undefined,
        stim_meds: form.stim_meds || undefined,
        amh: form.amh || undefined,
        prior_losses: form.prior_losses || undefined,
        retrieval_date: form.retrieval_date || undefined,
        transfer_date: form.transfer_date || undefined,
      },
      cycle_data: {
        months_trying: form.months_trying ? Number(form.months_trying) : undefined,
        avg_cycle_length: form.avg_cycle_length ? Number(form.avg_cycle_length) : undefined,
        last_period_date: form.last_period_date || undefined,
        tracking_method: form.tracking_method || undefined,
      },
      key_dates: {
        retrieval_date: form.retrieval_date || undefined,
        transfer_date: form.transfer_date || undefined,
      },
    });
    setEditing(false);
  }

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 };

  const diagnosisVal = proto.diagnosis?.trim() || 'Not added';
  const partnerVal = proto.partner_factor?.trim() || 'Not added';
  const priorLossesVal = proto.prior_losses?.trim() || 'Not added';
  const tryingSinceVal = (cycleData.months_trying != null && cycleData.months_trying > 0) ? `${cycleData.months_trying} mo` : 'Not added';
  const protocolVal = proto.protocol_type?.trim() || 'Not added';
  const stimMedsVal = proto.stim_meds?.trim() || 'Not added';
  const amhVal = proto.amh?.trim() || 'Not added';
  const afcVal = (proto.follicles != null && proto.follicles > 0) ? String(proto.follicles) : 'Not added';
  const hasRichStory =
    diagnosisVal !== 'Not added' ||
    protocolVal !== 'Not added' ||
    (proto.cycle_number != null && proto.cycle_number > 0);

  const sectionBorder = fertilityDemoSectionKeyline ? FERTILITY_DEMO_SECTION_BORDER : '0.5px solid rgba(212, 83, 126, 0.15)';

  return (
    <div style={{ fontFamily: fontBody }}>
      <div style={{ background: 'var(--white)', border: sectionBorder, borderRadius: 14, padding: '22px 24px', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <SectionHeader
              title={embedded ? 'My story' : 'My journey'}
              descriptor={
                embedded
                  ? 'Your fertility story — cycles, stims, and milestones.'
                  : 'Your fertility story — cycles, stims, and milestones. Nova uses this to personalize support.'
              }
            />
          </div>
          <button
            type="button"
            onClick={() => (editing ? save() : setEditing(true))}
            style={editing ? fertilityDemoSaveButton : fertilityDemoEditButton}
          >
            {editing ? 'Save' : 'Edit'}
          </button>
        </div>

        {editing ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
            {(isIVF || editing) && (
              <div style={{ background: 'rgba(255,255,255,0.55)', border: '0.5px solid rgba(212,83,126,0.15)', borderRadius: 12, padding: 18 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div><label style={labelStyle}>IVF cycle #</label><input type="number" min={1} value={form.cycle_number} onChange={(e) => setForm((f) => ({ ...f, cycle_number: e.target.value }))} style={inputStyle} placeholder="e.g. 2" /></div>
                  <div><label style={labelStyle}>Stim days (current)</label><input type="number" min={0} value={form.cycle_day} onChange={(e) => setForm((f) => ({ ...f, cycle_day: e.target.value }))} style={inputStyle} placeholder="e.g. 8" /></div>
                  <div><label style={labelStyle}>Follicles (last scan)</label><input type="number" min={0} value={form.follicles} onChange={(e) => setForm((f) => ({ ...f, follicles: e.target.value }))} style={inputStyle} placeholder="e.g. 12" /></div>
                  <div><label style={labelStyle}>Eggs retrieved</label><input type="number" min={0} value={form.eggs_retrieved} onChange={(e) => setForm((f) => ({ ...f, eggs_retrieved: e.target.value }))} style={inputStyle} placeholder="e.g. 10" /></div>
                  <div><label style={labelStyle}>Fertilized</label><input type="number" min={0} value={form.fertilized} onChange={(e) => setForm((f) => ({ ...f, fertilized: e.target.value }))} style={inputStyle} placeholder="e.g. 7" /></div>
                  <div><label style={labelStyle}>Embryos</label><input type="number" min={0} value={form.embryos} onChange={(e) => setForm((f) => ({ ...f, embryos: e.target.value }))} style={inputStyle} placeholder="e.g. 5" /></div>
                  <div><label style={labelStyle}>Total transfers so far</label><input type="number" min={0} value={form.transfers} onChange={(e) => setForm((f) => ({ ...f, transfers: e.target.value }))} style={inputStyle} placeholder="e.g. 1" /></div>
                  <div><label style={labelStyle}>Protocol</label><select value={form.protocol_type} onChange={(e) => setForm((f) => ({ ...f, protocol_type: e.target.value }))} style={inputStyle}><option value="">Select</option>{PROTOCOL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>
                  <div><label style={labelStyle}>Clinic</label><input type="text" value={form.clinic_name} onChange={(e) => setForm((f) => ({ ...f, clinic_name: e.target.value }))} style={inputStyle} placeholder="Clinic name" /></div>
                  <div><label style={labelStyle}>Diagnosis</label><select value={form.diagnosis} onChange={(e) => setForm((f) => ({ ...f, diagnosis: e.target.value }))} style={inputStyle}><option value="">Select</option>{DIAGNOSIS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>
                  <div><label style={labelStyle}>Partner factor</label><input type="text" value={form.partner_factor} onChange={(e) => setForm((f) => ({ ...f, partner_factor: e.target.value }))} style={inputStyle} placeholder="e.g. No male factor identified" /></div>
                  <div><label style={labelStyle}>Prior losses</label><input type="text" value={form.prior_losses} onChange={(e) => setForm((f) => ({ ...f, prior_losses: e.target.value }))} style={inputStyle} placeholder="Optional" /></div>
                  <div><label style={labelStyle}>Stim medications</label><input type="text" value={form.stim_meds} onChange={(e) => setForm((f) => ({ ...f, stim_meds: e.target.value }))} style={inputStyle} placeholder="e.g. Gonal-F, Menopur" /></div>
                  <div><label style={labelStyle}>AMH</label><input type="text" value={form.amh} onChange={(e) => setForm((f) => ({ ...f, amh: e.target.value }))} style={inputStyle} placeholder="e.g. 2.1 ng/mL" /></div>
                  <div><label style={labelStyle}>Retrieval date</label><input type="date" value={form.retrieval_date} onChange={(e) => setForm((f) => ({ ...f, retrieval_date: e.target.value }))} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Transfer date</label><input type="date" value={form.transfer_date} onChange={(e) => setForm((f) => ({ ...f, transfer_date: e.target.value }))} style={inputStyle} /></div>
                </div>
              </div>
            )}
            {(isTTC || editing) && (
              <div style={{ background: 'rgba(255,255,255,0.55)', border: '0.5px solid rgba(212,83,126,0.15)', borderRadius: 12, padding: 18 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div><label style={labelStyle}>Months trying</label><input type="number" min={0} value={form.months_trying} onChange={(e) => setForm((f) => ({ ...f, months_trying: e.target.value }))} style={inputStyle} placeholder="e.g. 6" /></div>
                  <div><label style={labelStyle}>Avg cycle length (days)</label><input type="number" min={21} max={45} value={form.avg_cycle_length} onChange={(e) => setForm((f) => ({ ...f, avg_cycle_length: e.target.value }))} style={inputStyle} placeholder="28" /></div>
                  <div><label style={labelStyle}>Last period date</label><input type="date" value={form.last_period_date} onChange={(e) => setForm((f) => ({ ...f, last_period_date: e.target.value }))} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Tracking method</label><input type="text" value={form.tracking_method} onChange={(e) => setForm((f) => ({ ...f, tracking_method: e.target.value }))} style={inputStyle} placeholder="OPKs, BBT, app, etc." /></div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ background: 'linear-gradient(140deg, rgb(251, 234, 240), rgb(225, 245, 238))', border: '0.5px solid rgba(212, 83, 126, 0.12)', borderRadius: 10, padding: '12px 14px' }}>
                <div className="card-label" style={{ fontWeight: 700, letterSpacing: '0.12em' }}>
                  <span>Diagnosis & history</span>
                </div>
                <DataRow label="Diagnosis" value={diagnosisVal} borderColor="rgba(212, 83, 126, 0.12)" isLast={false} />
                <DataRow label="Partner factor" value={partnerVal} borderColor="rgba(212, 83, 126, 0.12)" isLast={false} />
                <DataRow label="Trying since" value={tryingSinceVal} borderColor="rgba(212, 83, 126, 0.12)" isLast={false} />
                <DataRow label="Prior losses" value={priorLossesVal} borderColor="rgba(212, 83, 126, 0.12)" isLast={true} />
              </div>
              <div style={{ background: 'linear-gradient(140deg, rgb(225, 245, 238), rgb(230, 241, 251))', border: '0.5px solid rgba(29, 158, 117, 0.12)', borderRadius: 10, padding: '12px 14px' }}>
                <div className="card-label" style={{ fontWeight: 700, letterSpacing: '0.12em' }}>
                  <span>Current protocol</span>
                </div>
                <DataRowSage label="Protocol" value={protocolVal} isLast={false} />
                <DataRowSage label="Stim meds" value={stimMedsVal} isLast={false} />
                <DataRowSage label="AMH" value={amhVal} isLast={false} />
                <DataRowSage label="AFC" value={afcVal} isLast={true} />
              </div>
            </div>
            <div style={{ marginTop: 14, background: 'linear-gradient(135deg, rgba(251,234,240,0.6), rgba(225,245,238,0.6))', border: '0.5px solid rgba(212, 83, 126, 0.15)', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#D4537E', flexShrink: 0 }} />
              <div style={{ fontSize: 11, lineHeight: 1.5 }}>
                <span style={{ color: '#993556', fontWeight: 500 }}>Nova&apos;s context: </span>
                <span style={{ color: 'rgba(60,30,45,0.55)' }}>
                  {hasRichStory
                    ? 'Nova uses your cycle, protocol, clinic, and history here to personalize check-ins and reminders.'
                    : 'Add your IVF details above to personalize Nova.'}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {!embedded && (
        <div style={{ marginTop: 24 }}>
          <button type="button" onClick={() => navigate('/journey')} style={{ fontSize: 14, fontWeight: 700, color: 'var(--pink)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            Go to full journey tracker →
          </button>
        </div>
      )}
    </div>
  );
}
